import { Type, type Static, FormatRegistry } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  StringField,
  BooleanField,
  TimestampField,
  EnumField,
  UuidField,
} from '@ayocore/platform/common/helpers/schema-types';

import { name as moduleName } from '../module.json';
// Setup email format validation for TypeBox schemas
FormatRegistry.Set('email', value =>
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(
    value
  )
);

// Define base user fields (business logic only)
const UserBaseFields = {
  email: StringField({
    title: 'Email',
    format: 'email',
    maxLength: 255,
    nullable: true,
    description:
      'Email адрес пользователя. Nullable: системные пользователи и OAuth-аккаунты могут не иметь email.',
  }),
  password_hash: StringField({
    title: 'Хеш пароля',
    minLength: 60,
    maxLength: 60,
    nullable: true,
    private: true,
    description: 'Bcrypt хеш пароля. Nullable: системные пользователи не логинятся через пароль.',
  }),
  username: StringField({
    title: 'Username',
    maxLength: 50,
    nullable: true,
    description:
      'Уникальный handle пользователя (lowercase, a-z0-9_-). Используется для логина и отображения.',
  }),
  system_code: StringField({
    title: 'Системный код',
    maxLength: 64,
    nullable: true,
    description:
      'Идентификатор системного пользователя (UPPERCASE). Только для agent/cron/bulk-акторов.',
  }),
  name: StringField({
    title: 'Имя',
    minLength: 1,
    maxLength: 255,
    description: 'Полное имя пользователя',
  }),
  is_active: BooleanField({
    title: 'Активен',
    default: true,
    description: 'Активен ли аккаунт пользователя',
  }),
  last_login_at: TimestampField({
    title: 'Последний вход',
    nullable: true,
    description: 'Временная метка последнего входа',
  }),
  email_verified_at: TimestampField({
    title: 'Email подтвержден',
    nullable: true,
    description: 'Временная метка подтверждения email',
  }),
  api_token_hash: StringField({
    title: 'Хеш API токена',
    nullable: true,
    maxLength: 255,
    private: true,
    description: 'HMAC-SHA256 хеш API токена для Bearer авторизации (MCP)',
  }),
  api_token_expires_at: TimestampField({
    title: 'Срок действия API токена',
    nullable: true,
    description: 'Временная метка истечения API токена',
  }),
  oauth_user_id: StringField({
    title: 'OAuth User ID',
    maxLength: 255,
    nullable: true,
    unique: true,
    description:
      'UUID пользователя на OAuth-сервере (sub из JWT = oauth_users.uuid). Заполнено только для federated пользователей (auth_mode = federated).',
  }),
  auth_mode: EnumField(['local', 'federated'], {
    title: 'Режим аутентификации',
    nullable: true,
    description:
      'Способ аутентификации пользователя. NULL = local (backward compat для существующих записей).',
  }),
  avatar_thumb_file_uuid: UuidField({
    title: 'UUID аватара (миниатюра)',
    defaultRaw: 'NULL',
    nullable: true,
    description: 'UUID файла в filemanager_uploaded_files — миниатюра 64×64',
  }),
  avatar_medium_file_uuid: UuidField({
    title: 'UUID аватара (средний)',
    defaultRaw: 'NULL',
    nullable: true,
    description: 'UUID файла в filemanager_uploaded_files — аватар 256×256',
  }),
};

// Base schema (without system fields)
export const UserBaseSchema = Type.Object(UserBaseFields);

// Complete schema (with system fields)
export const UserSchema = Type.Object({
  ...SystemFields, // Platform-provided system fields (id, uuid, created_at, updated_at, sort_order)
  ...UserBaseSchema.properties,
});

// Derived schemas for different operations
export const UserCreateSchema = Type.Object({
  email: UserBaseFields.email,
  password: Type.String({
    title: 'Пароль',
    minLength: 8,
    maxLength: 255,
    description: 'Пароль в открытом виде (будет захеширован)',
  }),
  name: UserBaseFields.name,
  username: Type.Optional(UserBaseFields.username),
});

export const UserUpdateSchema = Type.Partial(
  Type.Object({
    email: UserBaseFields.email,
    name: UserBaseFields.name,
    username: UserBaseFields.username,
    is_active: UserBaseFields.is_active,
  })
);

export const UserLoginSchema = Type.Object({
  emailOrUsername: Type.String({
    title: 'Email или username',
    minLength: 1,
    maxLength: 255,
    description: 'Email адрес или username пользователя',
  }),
  password: Type.String({
    title: 'Пароль',
    minLength: 1,
    description: 'Пароль пользователя для аутентификации',
  }),
});

// TypeScript types derived from schemas
export type User = Static<typeof UserSchema>;
export type UserCreate = Static<typeof UserCreateSchema>;
export type UserUpdate = Static<typeof UserUpdateSchema>;
export type UserLogin = Static<typeof UserLoginSchema>;

// Schema definition with automatic table prefixing
export default defineSchema({
  moduleName,
  tableName: 'users', // System will prefix with 'identity_'
  schema: UserSchema,
  additionalIndexes: [
    'email',
    // Sparse unique index: один shadow user на один OAuth-аккаунт; множественные NULL разрешены
    { columns: ['oauth_user_id'], unique: true },
  ],
  foreignKeys: [
    {
      column: 'avatar_thumb_file_uuid',
      references: { table: 'filemanager_uploaded_files', column: 'uuid' },
      onDelete: 'SET NULL',
    },
    {
      column: 'avatar_medium_file_uuid',
      references: { table: 'filemanager_uploaded_files', column: 'uuid' },
      onDelete: 'SET NULL',
    },
  ],
  dependencies: ['filemanager_uploaded_files'],
});
